import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  TrendingUp, Users, Compass, Database, Cog, Landmark, ShieldCheck, Target,
  MessageSquare, Sparkles, Layers, Search, HelpCircle, BookOpen,
  Activity, LineChart, Truck, Factory, Wallet, Users2, Lightbulb, BarChart3,
  AlertTriangle, Handshake, Rocket, Eye,
  Award, Scale, Globe,
  Building2, HeartPulse,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import StrategyNetwork from "@/components/heroes/SignalFromNoise";

const NEEDS = [
  { icon: TrendingUp, title: "Market pressure", body: "The market is moving faster than most organisations were built to move. Standing still is now its own kind of risk." },
  { icon: Users, title: "Competitive pressure", body: "Rivals are experimenting publicly. Leadership teams need a clear, defensible view of where they will play and where they will not." },
  { icon: Wallet, title: "Unclear investment priorities", body: "The number of AI options is expanding faster than the business can evaluate. A strategy is needed to focus scarce capital and attention." },
  { icon: Database, title: "Data readiness", body: "AI is only as good as the data it is built on. Leadership needs to understand what is actually ready to be used, and what is not." },
  { icon: Cog, title: "Business process readiness", body: "Many of the largest AI gains come from redesigning the process around the technology, not the other way around." },
  { icon: Landmark, title: "Governance", body: "AI decisions carry regulatory, ethical and reputational weight. Leadership needs the oversight to move with confidence." },
  { icon: ShieldCheck, title: "Risk", body: "AI introduces new categories of business risk. They can be managed, but only if leadership sees them clearly." },
  { icon: Target, title: "Executive alignment", body: "Programmes that stall almost always lacked alignment at the top. Alignment is the first move, not the last." },
];

const QUESTIONS = [
  { icon: Compass, title: "Where should we start with AI?", body: "Which one or two use-cases will teach the organisation quickly and pay for the effort of learning?" },
  { icon: LineChart, title: "Which AI initiatives will deliver measurable business value?", body: "Where is the link between the AI investment and a business result the board can point to?" },
  { icon: Cog, title: "Are our business processes ready?", body: "Which processes will amplify AI, and which will quietly cap it, no matter what tools are deployed?" },
  { icon: Database, title: "Do we have the right data?", body: "Where is our data actually usable, where is it not, and what is the shortest path to closing the gap?" },
  { icon: AlertTriangle, title: "How do we avoid expensive AI experiments?", body: "How do we run cheap, honest tests before committing capital, and stop the ones that will not pay?" },
  { icon: Layers, title: "How should AI fit into our business strategy?", body: "How do we make AI a natural extension of the plan we already have, rather than a parallel programme?" },
  { icon: Users2, title: "How do we prepare our workforce?", body: "What does our leadership team, and the wider organisation, need to learn to work confidently with AI?" },
  { icon: Landmark, title: "What governance should be in place?", body: "What guardrails, decision rights and reporting lines does the board need to move forward with confidence?" },
];

const FRAMEWORK = [
  { title: "Business vision", body: "Start with the business the leadership team is trying to build. AI is judged only by how well it serves that vision." },
  { title: "Opportunity assessment", body: "Map the places in the business where AI can plausibly change growth, margin, resilience or risk." },
  { title: "Business process review", body: "Understand the processes AI would touch, and what would need to change for the value to actually land." },
  { title: "AI readiness assessment", body: "A candid read on data, skills, technology and change capacity, no soft-lens diagnostics." },
  { title: "Executive prioritisation", body: "The leadership team commits to the two or three moves worth making first, and defers the rest." },
  { title: "Governance framework", body: "Decision rights, oversight and guardrails put in place before scale, not after." },
  { title: "Roadmap", body: "A prioritised, board-ready plan that sequences investment and expected outcomes over a defensible horizon." },
  { title: "Business outcomes", body: "Progress is judged in the board's own terms: growth, margin, resilience and risk reduced." },
];

const VALUE_AREAS = [
  { icon: Activity, title: "Operational Efficiency", body: "Fewer manual steps, faster cycle times and a cleaner, more predictable operating core." },
  { icon: Eye, title: "Executive Decision Support", body: "Sharper, more timely signals for the two or three decisions leadership carries every quarter." },
  { icon: MessageSquare, title: "Customer Experience", body: "A more consistent and personal front line, delivered at a scale the business can afford." },
  { icon: BarChart3, title: "Sales & Marketing", body: "Better targeting, better forecasting and less capital wasted on effort that never converts." },
  { icon: Truck, title: "Supply Chain", body: "Cleaner planning, earlier warnings and a supply base that is easier to manage under stress." },
  { icon: Factory, title: "Manufacturing Operations", body: "Higher yield, tighter quality and equipment that fails less often, and less expensively." },
  { icon: Wallet, title: "Finance", body: "Faster close, cleaner controls and a business that leadership can read at a glance." },
  { icon: Users2, title: "Human Resources", body: "Better hires, less friction in the employee experience and more time spent on the work that matters." },
  { icon: BookOpen, title: "Knowledge Management", body: "The institutional knowledge of the business surfaced, structured and made available to those who need it." },
  { icon: Lightbulb, title: "Business Intelligence", body: "A single, credible view of the business that leadership can trust to make consequential decisions." },
];

const MISTAKES = [
  { title: "Starting with technology instead of business goals", body: "Programmes chase the platform, not the problem, and quietly lose sight of what they were meant to change." },
  { title: "Trying to automate everything", body: "Ambition without discipline creates surface activity, and very little business result." },
  { title: "Ignoring change management", body: "AI reshapes how people work. Programmes that do not plan for that reality rarely finish." },
  { title: "Poor governance", body: "Without decision rights and guardrails, AI decisions drift, exposure quietly rises and the board loses its line of sight." },
  { title: "Lack of executive sponsorship", body: "Programmes that are delegated stall. Programmes that leadership owns finish." },
  { title: "No measurable business objectives", body: "Without a defensible business goal, success becomes anecdote and investment becomes hard to defend." },
  { title: "Poor data quality", body: "AI amplifies whatever the data underneath it is. Weak data compounds weak decisions." },
  { title: "Vendor-driven AI decisions", body: "The AI agenda gets shaped by suppliers rather than by the business, and the choices become hard to defend later." },
];

const WHY = [
  { icon: Target, title: "Business-first perspective", body: "Every recommendation begins with the business result AI is meant to create. Technology follows the strategy, never the other way around." },
  { icon: Scale, title: "Independent AI advisory", body: "No products to sell and no platforms to push. The only interest is the AI decision that is right for your business." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of AI partners, platforms and providers, free of commercial bias or hidden incentives." },
  { icon: Award, title: "27+ years executive leadership", body: "Judgement built in real boardrooms, on real programmes, with real commercial consequence, not theory." },
  { icon: Compass, title: "Practical technology strategy", body: "A grounded AI plan the business can genuinely execute, not a document that looks impressive and moves nothing." },
  { icon: Users, title: "Fractional CIO perspective", body: "Senior, board-level technology leadership on the cadence your business requires, without a permanent appointment." },
  { icon: Globe, title: "Experience across India, the UAE and the Gulf", body: "Direct experience of business conditions across the region, and how AI decisions play out in practice." },
  { icon: Rocket, title: "Long-term executive partnership", body: "A senior advisor who stays through delivery, accountable to the outcome, not just the advice." },
];

const OUTCOMES = [
  { title: "Better Executive Decisions", note: "Sharper, faster calls at the top." },
  { title: "Operational Excellence", note: "A leaner, more resilient core." },
  { title: "Investment Optimisation", note: "AI spend tied to business return." },
  { title: "Business Growth", note: "New revenue, capacity and markets." },
  { title: "Competitive Advantage", note: "Capability rivals cannot easily copy." },
  { title: "Risk Reduction", note: "AI risk brought under control." },
  { title: "AI Governance", note: "Decisions held to account." },
  { title: "Scalable Operations", note: "AI adopted at the pace of the business." },
];

const READINESS = [
  { icon: Users, title: "Leadership", body: "A clear-headed executive team that owns the AI agenda from vision to outcome." },
  { icon: Landmark, title: "Governance", body: "Decision rights, oversight and guardrails proportionate to the ambition." },
  { icon: Cog, title: "Business processes", body: "Processes that will amplify AI, not silently cap it after the pilot." },
  { icon: Database, title: "Data", body: "A working understanding of what the data can and cannot support today." },
  { icon: Users2, title: "People", body: "A workforce prepared to work alongside AI with confidence and judgement." },
  { icon: Layers, title: "Technology", body: "A technology core capable of carrying AI without being overwhelmed by it." },
  { icon: Sparkles, title: "Culture", body: "A culture that is willing to experiment, and disciplined enough to stop when the evidence says so." },
  { icon: Compass, title: "Long-term strategy", body: "A view of AI that fits inside a defensible five-to-ten year plan for the business." },
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "AI Strategy Advisory",
  serviceType: "Executive AI Strategy & Governance Advisory",
  description: "Practical AI strategies for growing businesses, mid-market companies and family-owned enterprises. Aftab Azad works with executive teams across India, the UAE and the Gulf Region to ensure AI creates measurable business value, not expensive experiments.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/services/ai-strategy",
};

export default function AIStrategy() {
  return (
    <>
      <Seo
        title="AI Strategy Advisory"
        description="AI strategy that creates business value, not just technology projects. Aftab Azad helps executive teams in growing businesses across India, the UAE and the Gulf Region build practical AI strategies aligned with business objectives and measurable outcomes."
        path="/services/ai-strategy"
        keywords={["AI Strategy Advisor", "AI Strategy Consultant", "Executive AI Advisor", "Fractional CIO", "AI Governance", "Enterprise AI Strategy", "Mid-Market AI Strategy", "Family-Owned Business AI"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="ai-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <StrategyNetwork className="pointer-events-none absolute inset-y-10 right-0 hidden h-[calc(100%-5rem)] w-[38%] max-w-[520px] pr-4 opacity-90 md:block lg:w-[40%]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">AI Strategy</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["AI strategy that creates", "business value,"]} lineClassName="font-light" />
            <KineticHeading lines={["not just technology projects."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Artificial intelligence is becoming a strategic business decision rather than another
            technology initiative. The question is no longer whether to adopt AI. It is where AI will
            create meaningful business value, reduce operational complexity and support long-term
            growth. Aftab Azad helps executive teams build practical AI strategies aligned with
            business objectives, operational priorities and measurable outcomes.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="ai-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/contact" data-testid="ai-hero-discuss"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Discuss Your AI Roadmap
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY LEADERS NEED AN AI STRATEGY */}
      <section data-testid="ai-needs" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders need an AI strategy</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Pressure to act,", "without a", "defensible plan."]} />
            </h2>
            <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-slate-600">
              Many organisations are being pushed to adopt AI without a clear view of where it belongs
              in their business. Successful AI initiatives begin with business strategy, not with a
              choice of technology.
            </p>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {NEEDS.map((n, i) => {
              const Icon = n.icon;
              return (
                <Reveal key={n.title} delay={(i % 4) * 0.05}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-bold leading-snug tracking-tight text-navy">{n.title}</h3>
                    <p className="mt-3 font-body text-sm leading-relaxed text-slate-600">{n.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — QUESTIONS BUSINESS LEADERS ASK */}
      <section data-testid="ai-questions" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Common AI questions business leaders ask</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The questions", "asked in every", "boardroom."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2">
            {QUESTIONS.map((q, i) => {
              const Icon = q.icon;
              return (
                <Reveal key={q.title} delay={(i % 2) * 0.05}>
                  <div className="flex h-full gap-5 rounded-[1.5rem] border border-slate-200 bg-surface p-8">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold leading-snug tracking-tight text-navy">{q.title}</h3>
                      <p className="mt-3 font-body leading-relaxed text-slate-600">{q.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — AI STRATEGY FRAMEWORK */}
      <section data-testid="ai-framework" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Our AI strategy framework</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["From business vision", "to business outcome."]} />
            </h2>
          </div>
          <div className="mt-16 max-w-3xl">
            {FRAMEWORK.map((s, i) => (
              <Reveal key={s.title} className="relative flex gap-6 pb-10 last:pb-0">
                {i < FRAMEWORK.length - 1 && <span aria-hidden className="absolute left-6 top-14 h-[calc(100%-2.5rem)] w-px bg-slate-200" />}
                <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-lg font-bold text-white">{i + 1}</div>
                <div className="pt-1.5">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHERE AI CREATES BUSINESS VALUE */}
      <section data-testid="ai-value" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Where AI can create business value</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Value, in the", "board's own", "terms."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {VALUE_AREAS.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={(i % 3) * 0.05}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-surface p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-bold leading-snug tracking-tight text-navy">{v.title}</h3>
                    <p className="mt-3 font-body leading-relaxed text-slate-600">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — COMMON MISTAKES */}
      <section data-testid="ai-mistakes" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Common mistakes organisations make</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Patterns to", "recognise early."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {MISTAKES.map((m) => (
              <Reveal key={m.title} className="border-t border-slate-200 pt-6">
                <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{m.title}</h3>
                <p className="mt-2 max-w-md font-body leading-relaxed text-slate-600">{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY WORK WITH AFTAB */}
      <section data-testid="ai-why" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders work with Aftab Azad</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-14 md:grid-cols-2">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} className="flex gap-6 border-t border-slate-200 pt-8">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 8 — BUSINESS OUTCOMES */}
      <section data-testid="ai-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — PREPARING YOUR BUSINESS FOR AI */}
      <section data-testid="ai-readiness" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Preparing your business for AI</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Readiness is not", "a purchase."]} />
            </h2>
            <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-slate-600">
              AI readiness is not about buying software. It is about eight things, in this order.
            </p>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {READINESS.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={(i % 4) * 0.05}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-surface p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-bold leading-snug tracking-tight text-navy">{r.title}</h3>
                    <p className="mt-3 font-body text-sm leading-relaxed text-slate-600">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        overline="Build an AI strategy before investing in AI"
        heading={["A defensible AI plan,", "before the next commitment."]}
        body="Whether your organisation is evaluating AI for the first time or looking to scale existing initiatives, let's discuss how a practical AI strategy can support your long-term business goals without unnecessary complexity or risk."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Explore Advisory Services", to: "/services/executive-advisory" }}
      />
    </>
  );
}
